import { Link } from 'react-router-dom'
import { useMyGiveaways } from '@/hooks/queries'
import { useOverlayStore } from '@/store/overlays'
import type { Giveaway } from '@/models/giveaway'
import { GiveawayFeed } from '@/components/giveaway/GiveawayFeed'
import { AccountPage } from './account/AccountPage'
import { SkeletonLine } from '@/components/ui/Skeleton'

/**
 * The organiser's own feed at `/my/giveaways`.
 *
 * A page rather than the overlay the Quasar app used: this is a destination
 * people link to, come back to and scroll — none of which a modal does well.
 */
export function MyGiveawaysPage() {
  const { data: giveaways = [], isPending } = useMyGiveaways()
  // Their reach, in one number: the count alone says nothing about whether the
  // giveaways are working.
  const entries = giveaways.reduce((sum, giveaway) => sum + (giveaway.total_entries ?? 0), 0)
  const setSelectedGiveaway = useOverlayStore((state) => state.setSelectedGiveaway)
  const openOverlay = useOverlayStore((state) => state.open)
  const editGiveaway = useOverlayStore((state) => state.editGiveaway)

  const select = (giveaway: Giveaway) => {
    setSelectedGiveaway(giveaway)
    openOverlay('giveaway-details')
  }

  return (
    <AccountPage
      title="My Giveaways"
      meta={
        isPending ? (
          <SkeletonLine width="14em" />
        ) : (
          `${giveaways.length} ${giveaways.length === 1 ? 'giveaway' : 'giveaways'} · ${entries.toLocaleString()} ${entries === 1 ? 'entry' : 'entries'}`
        )
      }
      action={
        // Was `cta next`, the wizard's footer "Next" button borrowed for a
        // page header: same weight as a step control, and it read as one.
        // Creating a giveaway is the whole point of this page, so it gets the
        // gold and says what it makes.
        <button
          type="button"
          className="account-primary btn-sheen"
          onClick={() => openOverlay('new-giveaway')}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" className="account-primary-glyph">
            <path d="M8 3.2v9.6M3.2 8h9.6" />
          </svg>
          New giveaway
        </button>
      }
    >
      <GiveawayFeed
        giveaways={giveaways}
        isPending={isPending}
        onSelect={select}
        skeletonCount={3}
        emptyTitle="No giveaways yet"
        emptyBody="Giveaways you create will be listed here with their live entry counts."
        renderCardAction={(giveaway) => (
          <div className="giveaway-card-actions">
            <button
              type="button"
              className="giveaway-card-edit"
              onClick={() => editGiveaway(giveaway)}
            >
              Edit
            </button>
            <Link className="giveaway-card-edit" to={`/g/${giveaway.get_slug}`}>
              Open
            </Link>
          </div>
        )}
      />
    </AccountPage>
  )
}
